import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // "ministries" | "media" | null
  const navRef = useRef(null);

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname, location.hash]);

  // Close dropdown if clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (!navRef.current) return;
      if (!navRef.current.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const toggleDropdown = (key) => setOpenDropdown((prev) => (prev === key ? null : key));

  const closeAll = () => {
    setMobileOpen(false);
    setOpenDropdown(null);
  };

  // ✅ Works from ANY route: navigates to /#id then scrolls
  const goToSection = async (hash) => {
    closeAll();

    // already on home -> just scroll
    if (location.pathname === "/") {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else window.location.hash = hash; // fallback
      return;
    }

    // not on home -> navigate to home with hash
    navigate(`/${hash}`);
  };

  // ✅ When we land on /#something, scroll smoothly
  useEffect(() => {
    if (location.pathname !== "/") return;
    if (!location.hash) return;

    const t = setTimeout(() => {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);

    return () => clearTimeout(t);
  }, [location.pathname, location.hash]);

  return (
    <header id="js-header" className="u-header u-header--static dark-header" ref={navRef}>
      <div className="u-header__section u-header__section--dark g-bg-primary g-transition-0_3 g-py-10">
        <nav className="navbar navbar-expand-lg hs-menu-horizontal gs-main-nav" aria-label="Main navigation">
          <div className="container">
            {/* Mobile toggler */}
            <button
              className="navbar-toggler navbar-toggler-right btn g-line-height-1 g-brd-none g-pa-0 g-pos-abs g-top-3 g-right-0"
              type="button"
              aria-label="Toggle navigation"
              aria-expanded={mobileOpen ? "true" : "false"}
              aria-controls="navBar"
              onClick={() => setMobileOpen((v) => !v)}
            >
              <span className="hamburger hamburger--slider g-pt-5 g-pr-0">
                <span className="hamburger-box">
                  <span className="hamburger-inner" />
                </span>
              </span>
            </button>

            <Link to="/" className="navbar-brand" onClick={closeAll}>
              <img src="/images/bishopLogo.jpg" alt="Bishop K.J. Brown Ministries" height="150" width="150" />
            </Link>

            <div className={`collapse navbar-collapse align-items-center flex-sm-row ${mobileOpen ? "show" : ""}`} id="navBar">
              <ul className="navbar-nav text-uppercase gs-main-nav-list g-font-weight-600 ml-auto">
                <li className="nav-item g-mx-15--lg">
                  <Link to="/" className="nav-link g-py-7 g-px-0" onClick={closeAll}>
                    HOME
                  </Link>
                </li>

                {/* ✅ About section is on HOME: /#about */}
                <li className="nav-item g-mx-15--lg">
                  <button
                    type="button"
                    className="nav-link g-py-7 g-px-0 text-uppercase"
                    style={{ background: "transparent", border: "none", textTransform: "uppercase" }}
                    onClick={() => goToSection("#about")}
                  >
                    ABOUT
                  </button>
                </li>

                <li className="nav-item g-mx-15--lg">
                  <Link to="/prayer" className="nav-link g-py-7 g-px-0" onClick={closeAll}>
                    PRAYER
                  </Link>
                </li>

                {/* Ministries */}
                <li className={`nav-item hs-has-sub-menu g-mx-15--lg ${openDropdown === "ministries" ? "hs-sub-menu-opened" : ""}`}>
                  <button
                    type="button"
                    className="nav-link g-py-7 g-px-0 text-uppercase"
                    style={{ background: "transparent", border: "none", textTransform: "uppercase" }}
                    aria-haspopup="true"
                    aria-expanded={openDropdown === "ministries" ? "true" : "false"}
                    onClick={() => toggleDropdown("ministries")}
                  >
                    MINISTRIES
                  </button>

                  <ul
                    className="hs-sub-menu list-unstyled u-shadow-v11 g-brd-top g-brd-primary g-brd-top-2 g-min-width-220 g-mt-18 g-mt-8--lg--scrolling"
                    style={{ display: openDropdown === "ministries" ? "block" : "none" }}
                  >
                    <li className="dropdown-item">
                      <button
                        type="button"
                        className="nav-link"
                        style={{ background: "transparent", border: "none", width: "100%", textAlign: "left" }}
                        onClick={() => goToSection("#mission")}
                      >
                        Mission
                      </button>
                    </li>
                    <li className="dropdown-item">
                      <Link to="/ladyp" className="nav-link" onClick={closeAll}>
                        Lady P
                      </Link>
                    </li>
                    <li className="dropdown-item">
                      <Link to="/members" className="nav-link" onClick={closeAll}>
                        Members
                      </Link>
                    </li>
                  </ul>
                </li>

                {/* Media */}
                <li className={`nav-item hs-has-sub-menu g-mx-15--lg ${openDropdown === "media" ? "hs-sub-menu-opened" : ""}`}>
                  <button
                    type="button"
                    className="nav-link g-py-7 g-px-0 text-uppercase"
                    style={{ background: "transparent", border: "none", textTransform: "uppercase" }}
                    aria-haspopup="true"
                    aria-expanded={openDropdown === "media" ? "true" : "false"}
                    onClick={() => toggleDropdown("media")}
                  >
                    MEDIA
                  </button>

                  <ul
                    className="hs-sub-menu list-unstyled u-shadow-v11 g-brd-top g-brd-primary g-brd-top-2 g-min-width-220 g-mt-18 g-mt-8--lg--scrolling"
                    style={{ display: openDropdown === "media" ? "block" : "none" }}
                  >
                    <li className="dropdown-item">
                      <button
                        type="button"
                        className="nav-link"
                        style={{ background: "transparent", border: "none", width: "100%", textAlign: "left" }}
                        onClick={() => goToSection("#podcast")}
                      >
                        Podcast
                      </button>
                    </li>
                    <li className="dropdown-item">
                      <button
                        type="button"
                        className="nav-link"
                        style={{ background: "transparent", border: "none", width: "100%", textAlign: "left" }}
                        onClick={() => goToSection("#news")}
                      >
                        News
                      </button>
                    </li>
                    <li className="dropdown-item">
                      <Link to="/media" className="nav-link" onClick={closeAll}>
                        Coming Soon
                      </Link>
                    </li>
                  </ul>
                </li>

                <li className="nav-item g-mx-15--lg">
                  <button
                    type="button"
                    className="nav-link g-py-7 g-px-0 text-uppercase"
                    style={{ background: "transparent", border: "none", textTransform: "uppercase" }}
                    onClick={() => goToSection("#salvation")}
                  >
                    SALVATION
                  </button>
                </li>

                <li className="nav-item g-mx-15--lg">
                  <Link to="/ai" className="nav-link g-py-7 g-px-0" onClick={closeAll}>
                    ASK?
                  </Link>
                </li>

                <li className="nav-item g-mx-15--lg">
                  <a
                    href="https://www.bishopkjbrownbooks.org"
                    className="nav-link g-py-7 g-px-0"
                    target="_blank"
                    rel="noreferrer"
                  >
                    STORE
                  </a>
                </li>
              </ul>
            </div>

            <a className="btn btn-outline-light ml-3" href="https://tithe.ly/give?c=278656" target="_blank" rel="noreferrer">
              Donate
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;